import { connect } from "react-redux";
import moment from "moment-timezone";
import React, { Component } from "react";
import { withRouter } from "react-router-dom";

import { getPatientProfile } from "@/redux/actions/auth";
import { getQueryParams } from "@/utils/filters";
import images from "@/utils/images";
import { isAuthorized, isUserPrescriber } from "@/utils/authToken";
import { messagingRequests } from "@/api/(requests)/messaging";
import { providerRequests } from "@/api/(requests)/provider";
import Spinner from "@/components/UI/Spinner";
import { SUPPORT_NUMBER, SUPPORT_NUMBER_HREF } from "@/constants/aptihealth";

import AddMessageChannel from "./AddMessageChannel";
import { MessagesContainer } from "./MessagesContainer";

import "./styles.scss";

class MessagesView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      channels: [],
      initialLoadComplete: false,
      isInactive: false,
      isMobile: window.innerWidth <= 640,
      loadingMoreChannels: false,
      mobileView: "CHANNEL_SELECT",
      nextChannelPageQuery: null,
      sendbirdConnectionIssue: false,
    };
  }

  async componentDidMount() {
    const { getPatientProfile } = this.props;

    if (isAuthorized("user:patient")) {
      await getPatientProfile();
    }

    if (
      isAuthorized("user:patient") &&
      this.props.profile !== undefined &&
      this.props.profile.status === "INACTIVE"
    ) {
      this.setState({ isInactive: true });
    }
    await this.fetchMessagingInfo();
    await this.processChannels();

    window.addEventListener("resize", this.handleResize);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.profile === undefined &&
      this.props.profile !== undefined &&
      this.props.profile.status === "INACTIVE"
    ) {
      this.setState({ isInactive: true });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
    this.state.client.removeAllChannelHandlers();
    this.state.client.disconnect();
  }

  fetchMessagingInfo = async () => {
    let user = await messagingRequests.fetch_user({ context: { showLoader: false } });
    let contacts = await messagingRequests.fetch_contacts({ context: { showLoader: false } });
    let client = messagingRequests.create_client();

    try {
      await messagingRequests.connect_to_sendbird(client, user.user_id, user.access_token);
    } catch (err) {
      console.error("Sendbird connection issue:", err);
      this.setState({ sendbirdConnectionIssue: true });
      throw err;
    }

    const { channels, nextChannelPageQuery } =
      await messagingRequests.get_paginated_channels(client);
    const processedChannels = channels.map((channel) => this.processChannel(user, channel));

    const ChannelHandler = new client.ChannelHandler();

    ChannelHandler.onUserMuted = (channel) => {
      this.processUpdatedChannel(channel);
    };
    ChannelHandler.onUserUnmuted = (channel) => {
      this.processUpdatedChannel(channel);
    };
    client.addChannelHandler("MESSAGE_VIEW_INDEX_HANDLER", ChannelHandler);

    this.setState({
      channels: processedChannels,
      client,
      contacts,
      nextChannelPageQuery,
      user,
    });

    return user;
  };

  processChannels = async () => {
    let selectedChannel, isAddingChannel;
    const memberId = getQueryParams("memberId", this.props.location.search);
    let foundChannel;
    const channels = this.state.channels;

    if (memberId) {
      foundChannel = channels.find(
        (channel) =>
          channel.otherMembersMap &&
          Object.keys(channel.otherMembersMap).length === 1 &&
          Object.hasOwn(channel.otherMembersMap, memberId),
      );
    }

    if (memberId && !foundChannel) {
      await this.handleDirectMessageSelection({ user_id: memberId });
      this.setState({
        initialLoadComplete: true,
      });

      return;
    } else if (foundChannel) {
      selectedChannel = foundChannel;
      isAddingChannel = false;
    } else if (channels.length > 0) {
      selectedChannel = channels[0];
      isAddingChannel = false;
    } else {
      selectedChannel = null;
      isAddingChannel = true;
    }

    this.setState({
      initialLoadComplete: true,
      isAddingChannel,
      selectedChannel,
    });
  };

  handleResize = () => {
    this.setState((state) => {
      let isMobile = window.innerWidth <= 640;

      if (state.isMobile !== isMobile) {
        return {
          isMobile,
          mobileView: "CHANNEL_SELECT",
        };
      }
    });
  };

  processChannel = (user, channel) => {
    channel.otherMembers = channel.members.filter((member) => member.userId !== user.user_id);

    channel.otherMembersMap = channel.otherMembers.reduce((map, member) => {
      map[member.userId] = member;

      return map;
    }, {});

    if (channel.otherMembers.length === 1) {
      let otherMember = channel.otherMembers[0];

      channel.coverImage =
        otherMember.profileUrl !== "" ? otherMember.profileUrl : images("./common/avatar.png");
    }

    if (channel.lastMessage) {
      let createdAt = moment(channel.lastMessage.createdAt);
      let format = createdAt.isSame(moment(), "day") ? "hh:mm A" : "MM/DD/YY hh:mm A";

      channel.lastMessage.formattedTime = createdAt.format(format);
    }

    return channel;
  };

  showMoreChannels = async () => {
    const { nextChannelPageQuery, user } = this.state;

    this.setState({ loadingMoreChannels: true });

    const { channels: newChannels, nextChannelPageQuery: newNextChannelPageQuery } =
      await messagingRequests.get_next_channel_page(nextChannelPageQuery);
    const processedChannels = newChannels.map((channel) => this.processChannel(user, channel));

    this.setState({
      channels: [...this.state.channels, ...processedChannels],
      loadingMoreChannels: false,
      nextChannelPageQuery: newNextChannelPageQuery,
    });
  };

  switchMessagesOrAddChannelView = () => {
    this.setState((state) => {
      // If there are no channels do not allow to leave addChannelView
      let isAddingChannel = this.state.channels.length === 0 ? true : !state.isAddingChannel;

      return {
        isAddingChannel,
      };
    });
  };

  mutePatientIfNeeded = async (processedChannel, patientId) => {
    if (!isUserPrescriber()) {
      return;
    }

    const urlParams = { patientId };
    const data = {
      channel_url: processedChannel.url,
      patient_read_only_message_thread: true,
    };

    await providerRequests.toggle_patient_read_only_message_thread({
      data,
      urlParams,
    });
  };

  handleDirectMessageSelection = async (contact) => {
    let userIds = [this.state.user.user_id, contact.user_id];
    let upsertChannel = await messagingRequests.upsert_distinct_channel(this.state.client, userIds);
    let channels = Array.from(this.state.channels);

    // Put selected channel at the top
    let existingChannelIndex = channels.findIndex(
      (channel) => channel.url === upsertChannel.channel.url,
    );

    if (upsertChannel.isCreated || existingChannelIndex === -1) {
      await this.mutePatientIfNeeded(upsertChannel.channel, contact.user_id);
      let newChannel = this.processChannel(this.state.user, upsertChannel.channel);

      channels.unshift(newChannel);
    } else {
      let processedExistingChannel = this.processChannel(this.state.user, upsertChannel.channel);

      channels.splice(existingChannelIndex, 1);
      channels.unshift(processedExistingChannel);
    }

    this.setState({
      channels,
      isAddingChannel: false,
      mobileView: "CHAT",
      selectedChannel: upsertChannel.channel,
    });
  };

  onSelectChannel = (channel) => {
    this.setState({
      mobileView: "CHAT",
      selectedChannel: channel,
    });
  };

  processUpdatedChannel = (updatedChannel) => {
    const nonUpdatedChannels = Array.from(this.state.channels).filter(
      (channel) => channel.url !== updatedChannel.url,
    );
    const processedUpdatedChannel = this.processChannel(this.state.user, updatedChannel);

    this.setState({
      channels: [processedUpdatedChannel, ...nonUpdatedChannels],
    });
  };

  renderSendbirdUnavailableError = () => {
    return (
      <div className="d-flex text-center tw:justify-center">
        <div className="my-5 mx-2" style={{ maxWidth: 580 }}>
          <p className="fs-26 my-3 tw:font-bold">aptihealth messaging is currently unavailable.</p>
          <p className="fs-18">
            We apologize for the inconvenience. If you need to reach someone, contact support at{" "}
            <a className="Messages--error-link" href={SUPPORT_NUMBER_HREF}>
              {SUPPORT_NUMBER}
            </a>{" "}
            or{" "}
            <a className="Messages--error-link" href="mailto:support@aptihealth.com">
              support@aptihealth.com
            </a>
            .
          </p>

          <br />

          <p className="fs-18">
            Go to the{" "}
            <a className="Messages--error-link" href="/app/home" target="_self">
              dashboard
            </a>
            .
          </p>
        </div>
      </div>
    );
  };

  render() {
    if (this.state.sendbirdConnectionIssue) {
      return this.renderSendbirdUnavailableError();
    }

    if (!this.state.initialLoadComplete) {
      return <Spinner />;
    }

    if (this.state.isAddingChannel) {
      return (
        <AddMessageChannel
          channels={this.state.channels}
          contacts={this.state.contacts}
          handleSelect={this.handleDirectMessageSelection}
          switchView={this.switchMessagesOrAddChannelView}
        />
      );
    }

    return (
      <MessagesContainer
        channels={this.state.channels}
        client={this.state.client}
        fetchMessagingInfo={this.fetchMessagingInfo}
        isInactive={this.state.isInactive}
        isMobile={this.state.isMobile}
        loadingMoreChannels={this.state.loadingMoreChannels}
        mobileView={this.state.mobileView}
        nextChannelPageQuery={this.state.nextChannelPageQuery}
        onMessageReceived={this.processUpdatedChannel}
        onSelectChannel={this.onSelectChannel}
        selectedChannel={this.state.selectedChannel}
        showMoreChannels={this.showMoreChannels}
        switchMessagesOrAddChannelView={this.switchMessagesOrAddChannelView}
        switchMobileView={() => this.setState({ mobileView: "CHANNEL_SELECT" })}
        user={this.state.user}
      />
    );
  }
}

const mapStateToProps = (state) => {
  return {
    flags: state.flags,
    profile: state.auth.profile,
  };
};

export default connect(mapStateToProps, {
  getPatientProfile,
})(withRouter(MessagesView));
